/** Cross-session audit dashboard — Phase 2A-aud Tier 2.
 *
 * Lists every session in the active org with an audit chain (Phase
 * 1B-D or newer). Lightweight summary per row (event count + token
 * counts); click through to `/sessions/{id}/audit` for the full chain
 * walk + tamper detection.
 *
 * Filterable by Session.status. Date-range + pagination shipped behind
 * a cursor on `created_at` (descending). Gated on `audit.verify`.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { AppLayout } from "@/components/AppLayout";
import { PermissionGate } from "@/components/PermissionGate";
import { fetchAuditSessions } from "@/modules/audit/api";
import { SessionSummaryRow } from "@/modules/audit/SessionSummaryRow";


const STATUS_OPTIONS = [
  { value: "", key: "ui.auditDashboard.statusAll" },
  { value: "created", key: "ui.auditDashboard.statusCreated" },
  { value: "live", key: "ui.auditDashboard.statusLive" },
  { value: "ended", key: "ui.auditDashboard.statusEnded" },
];


export default function AuditDashboardPage() {
  const { t } = useTranslation();
  return (
    <AppLayout
      title={t("ui.auditDashboard.title")}
      description={t("ui.auditDashboard.description")}
    >
      <PermissionGate
        perm="audit.verify"
        fallback={
          <p className="card text-sm text-nb-text-2">
            <Trans i18nKey="ui.auditDashboard.permFallback">
              You need <code>audit.verify</code> to view this page.
            </Trans>
          </p>
        }
      >
        <DashboardBody />
      </PermissionGate>
    </AppLayout>
  );
}


function DashboardBody() {
  const { t } = useTranslation();
  const [status, setStatus] = useState("");
  const [cursor, setCursor] = useState<string | undefined>();

  const q = useQuery({
    queryKey: ["audit-sessions", status, cursor],
    queryFn: () =>
      fetchAuditSessions({
        status: status || undefined,
        beforeCreatedAt: cursor,
        limit: 30,
      }),
    placeholderData: (prev) => prev,
  });

  return (
    <div className="space-y-3">
      <section className="card flex flex-wrap items-baseline gap-3">
        <label className="flex items-baseline gap-2 text-sm">
          <span className="text-nb-text-2">{t("ui.auditDashboard.statusLabel")}</span>
          <select
            className="input"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setCursor(undefined);
            }}
            aria-label={t("ui.auditDashboard.filterAria")}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {t(o.key)}
              </option>
            ))}
          </select>
        </label>
      </section>

      <section className="card space-y-2">
        <div className="grid grid-cols-12 gap-3 border-b border-nb-border px-4 pb-2 text-xs uppercase tracking-wide text-nb-text-3">
          <span className="col-span-5">{t("ui.auditDashboard.colSession")}</span>
          <span className="col-span-2">{t("ui.auditDashboard.colStatus")}</span>
          <span className="col-span-2">{t("ui.auditDashboard.colCreated")}</span>
          <span className="col-span-1 text-right">{t("ui.auditDashboard.colEvents")}</span>
          <span className="col-span-2 text-right">{t("ui.auditDashboard.colAnchors")}</span>
        </div>
        {q.isLoading && (
          <p className="px-4 py-3 text-sm text-nb-text-2">{t("ui.auditDashboard.loading")}</p>
        )}
        {q.isError && (
          <p className="px-4 py-3 text-sm text-cvp-danger">
            {(q.error as Error).message}
          </p>
        )}
        {q.data && (
          <>
            {q.data.results.length === 0 ? (
              <p className="px-4 py-3 text-sm text-nb-text-2">
                {t("ui.auditDashboard.noResults")}
              </p>
            ) : (
              <ul className="divide-y divide-nb-border">
                {q.data.results.map((row) => (
                  <SessionSummaryRow key={row.session_id} row={row} />
                ))}
              </ul>
            )}
          </>
        )}
      </section>

      <div className="flex items-baseline justify-between text-xs text-nb-text-3">
        <span>
          {q.data
            ? t("ui.auditDashboard.sessionCount", { count: q.data.results.length })
            : ""}
        </span>
        <div className="flex gap-2">
          {cursor && (
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => setCursor(undefined)}
            >
              ← {t("ui.auditDashboard.firstPage")}
            </button>
          )}
          {q.data?.has_more && q.data.next_before_created_at && (
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => setCursor(q.data?.next_before_created_at ?? undefined)}
            >
              {t("ui.auditDashboard.older")} →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
